// @flow
import * as React from 'react';
import Box from 'components/Box';
import Tooltip from 'components/Tooltip';
import {
  AutocompleteTextInputField,
  CheckboxField,
  InputField,
  LocationField,
  MultiInputField,
  FormContext
} from 'components/Form';

const { useContext, useRef } = React;

const sanitizeTitle = value =>
  value
    .toUpperCase()
    .replace(/[^A-Z\s]/g, '')
    .trim()
    .replace(/\s+/g, ' ');

const JobTitleField = ({
  name,
  label,
  description,
  placeholder
}: {
  name: string,
  label: React.Node,
  description: string,
  placeholder: string
}) => {
  const inputRef = useRef(null);

  const { updateValueWith } = useContext(FormContext) || {};

  const onSuggestionSelected = (_, { suggestion }) => {
    updateValueWith(name, () => ({
      synonymId: suggestion.value.objectID,
      text: suggestion.name
    }));
  };

  const onBlur = (e, suggestions) => {
    if (suggestions && suggestions.length) {
      if (
        sanitizeTitle(e.target.value) === sanitizeTitle(suggestions[0].name)
      ) {
        updateValueWith(name, () => ({
          synonymId: suggestions[0].value.objectID,
          text: suggestions[0].name
        }));
      }
    }
  };

  return (
    <Box mb={30}>
      <div ref={inputRef}>
        <AutocompleteTextInputField
          name={`${name}.text`}
          label={label}
          description={description}
          placeholder={placeholder}
          autocompleteType="SALARY_SURVEY_TITLES"
          shouldRenderSuggestions={v => v && v.toString().trim().length >= 3}
          onBlur={onBlur}
          onSuggestionSelected={onSuggestionSelected}
          onChange={e => {
            updateValueWith(name, () => ({
              synonymId: null,
              text: e.target.value
            }));
          }}
          large
        />
      </div>
    </Box>
  );
};

const ApplicantProfile = ({
  values,
  errors
}: {
  values: Object,
  errors?: APIErrors
}) => (
  <>
    <JobTitleField
      name="applicantProfile.currentJobTitle"
      label={
        /* TRANSLATORS: tooltip for resume db current job title */
        <Box display="flex" alignItems="center">
          {getText('Job Title')}
          {'*'}
          <Box ml="4px">
            <Tooltip>
              {getText(
                `Use the field below to see title & description recommendations
              that may describe your role. Don't worry about a perfect match!
              Try similar titles to select a close fit.`
              )}
            </Tooltip>
          </Box>
        </Box>
      }
      description={getText(
        'Your current or most recent job title. Start typing to see suggestions.'
      )}
      placeholder={getText('Executive Assistant')}
    />
    <MultiInputField
      addText={
        /* TRANSLATORS: Applicant wants to add a prospective job title */ getText(
          "Add another title I'm qualified for"
        )
      }
      removeText={
        /* TRANSLATORS: Applicant wants to remove a prospective job title */ getText(
          'Remove this title'
        )
      }
      component={JobTitleField}
      defaultData={{}}
      name="applicantProfile.jobTitles"
      hideControls
      showEmpty
      startWithNone
      maxInputs={5}
      errors={errors}
      extraData={new Array(5).fill().map(() => ({
        placeholder: 'Executive Assistant'
      }))}
    />
    <LocationField
      customErrorMatch={apiErrors =>
        apiErrors && apiErrors['applicantProfile.currentLocation.full']
      }
      label={`${getText('Current Location')}*`}
      description={getText('The city where you currently live.')}
      includeCity
      includeFull
      name="applicantProfile.currentLocation"
      placeholder="City, State"
    />
    <CheckboxField
      name="applicantProfile.canRelocate"
      label={
        /* TRANSLATORS: Applicant is willing to relocate for a job */
        <Box display="flex" alignItems="center">
          {getText("I'm willing to relocate.")}
        </Box>
      }
    />
    {values && values.applicantProfile && values.applicantProfile.canRelocate && (
      <MultiInputField
        addText={
          /* TRANSLATORS: Applicant wants to add a place they are willing to go for a job */ getText(
            'Add Another Location'
          )
        }
        removeText={
          /* TRANSLATORS: Applicant wants to remove a place they are willing to go for a job */ getText(
            'Remove this location'
          )
        }
        component={LocationField}
        defaultData={{}}
        name="applicantProfile.locations"
        hideControls
        maxInputs={4}
        extraData={new Array(5).fill().map((value, index) => ({
          customErrorMatch: apiErrors =>
            apiErrors && apiErrors[`applicantProfile.locations.${index}.text`],
          includeCity: true,
          includeFull: true,
          placeholder: 'City, State'
        }))}
        errors={errors}
      />
    )}
    <InputField
      name="applicantProfile.yearsOfExperience"
      label={
        /* TRANSLATORS: Compensation survey */
        `${getText('Years of experience in current or similar functions')}*`
      }
      description={getText('Indicate total years in this field')}
      type="number"
      placeholder={getText('8')}
      min={0}
      max={100}
      noWrap
    />
    <CheckboxField
      name="applicantProfile.managerialStatus"
      label={
        /* TRANSLATORS: Compensation survey */

        <Box display="flex" alignItems="center">
          {getText('I supervise(d) employees in this job')}
          <Box position="relative" zIndex="1" ml="4px">
            <Tooltip>
              {getText(
                `Supervision includes part- or full-time employees, as well as interns.`
              )}
            </Tooltip>
          </Box>
        </Box>
      }
    />
  </>
);

export default ApplicantProfile;
